import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  FormControl,
  Button,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyLead = {
  leadId: "",
  storeId: "",
  contactId: "",
  source: "",
  status: "",
  assignedUserId: "",
  expectedValue: "",
  priority: "",
  leadStage: "",
  nextActionDate: "",
  description: "",
};

export default class CrmLeads extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      leads: [],
      filteredLeads: [],
      loading: true,
      search: "",
      error: null,
      showModal: false,
      isEdit: false,
      currentLead: { ...emptyLead },
    };
  }

  componentDidMount() {
    this.loadLeads();
  }

  loadLeads = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const leads = await fetchCrmList("Leads", "GetLeads", storeId);
      const normalized = Array.isArray(leads) ? leads : [];
      this.setState({
        leads: normalized,
        filteredLeads: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load leads.",
      });
    }
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const normalizedSearch = search.trim().toLowerCase();
    const filteredLeads = this.state.leads.filter((lead) => {
      const name =
        `${lead.firstName || ""} ${lead.lastName || ""}`.toLowerCase();
      const email = (lead.email || "").toLowerCase();
      const status = (lead.status || "").toLowerCase();
      return (
        name.includes(normalizedSearch) ||
        email.includes(normalizedSearch) ||
        status.includes(normalizedSearch)
      );
    });

    this.setState({ search, filteredLeads });
  };

  formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  };

  handleShowModal = (lead = null) => {
    this.setState({
      currentLead: lead ? { ...lead } : { ...emptyLead },
      isEdit: Boolean(lead),
      showModal: true,
      error: null,
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentLead: {
        ...prevState.currentLead,
        [field]: value,
      },
    }));
  };

  saveLead = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const lead = {
      ...this.state.currentLead,
      storeId,
      leadId:
        this.state.currentLead.leadId || "00000000-0000-0000-0000-000000000000",
      expectedValue: this.state.currentLead.expectedValue
        ? Number(this.state.currentLead.expectedValue)
        : null,
      nextActionDate: this.state.currentLead.nextActionDate || null,
    };

    try {
      await saveCrmItem("Leads", "AddOrUpdateLead", lead);
      await this.loadLeads();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save lead." });
    }
  };

  deleteLead = async (leadId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this lead?")) return;

    try {
      await deleteCrmItem("Leads", "DeleteLead", storeId, leadId);
      await this.loadLeads();
    } catch (error) {
      this.setState({ error: error.message || "Unable to delete lead." });
    }
  };

  render() {
    const {
      filteredLeads,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentLead,
    } = this.state;
    const storeId = this.context.storeId;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Leads</h2>
            <p className="text-muted">Manage leads for the selected store.</p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Lead
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadLeads}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM leads.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by name, email, or status"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredLeads.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-5">
                  No leads found.
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.leadId || lead.email || Math.random()}>
                  <td>
                    {`${lead.firstName || ""} ${lead.lastName || ""}`.trim()}
                  </td>
                  <td>{lead.email || "-"}</td>
                  <td>{lead.phone || "-"}</td>
                  <td>{lead.status || "-"}</td>
                  <td>{this.formatDate(lead.createdAt || lead.createdOn)}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(lead)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.deleteLead(lead.leadId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEdit ? "Edit Lead" : "Add Lead"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="leadFirstName">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      value={currentLead.firstName}
                      onChange={(e) =>
                        this.handleFieldChange("firstName", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadLastName">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      value={currentLead.lastName}
                      onChange={(e) =>
                        this.handleFieldChange("lastName", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={currentLead.email}
                      onChange={(e) =>
                        this.handleFieldChange("email", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadPhone">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      value={currentLead.phone}
                      onChange={(e) =>
                        this.handleFieldChange("phone", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      value={currentLead.status}
                      onChange={(e) =>
                        this.handleFieldChange("status", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadSource">
                    <Form.Label>Source</Form.Label>
                    <Form.Control
                      value={currentLead.source}
                      onChange={(e) =>
                        this.handleFieldChange("source", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadPriority">
                    <Form.Label>Priority</Form.Label>
                    <Form.Control
                      value={currentLead.priority}
                      onChange={(e) =>
                        this.handleFieldChange("priority", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadStage">
                    <Form.Label>Lead Stage</Form.Label>
                    <Form.Control
                      value={currentLead.leadStage}
                      onChange={(e) =>
                        this.handleFieldChange("leadStage", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadExpectedValue">
                    <Form.Label>Expected Value</Form.Label>
                    <Form.Control
                      type="number"
                      value={currentLead.expectedValue}
                      onChange={(e) =>
                        this.handleFieldChange("expectedValue", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="leadNextActionDate">
                    <Form.Label>Next Action</Form.Label>
                    <Form.Control
                      type="date"
                      value={currentLead.nextActionDate?.split("T")[0] || ""}
                      onChange={(e) =>
                        this.handleFieldChange("nextActionDate", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="leadDescription">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={currentLead.description}
                      onChange={(e) =>
                        this.handleFieldChange("description", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveLead}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
